import { Link } from "react-router-dom";
import {
  CreditCard,
  Wrench,
  LifeBuoy,
  ArrowUpRight,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";

const categories = [
  {
    icon: CreditCard,
    title: "Maintaining Your Account",
    testid: "care-category-account",
    links: [
      { label: "Make a Payment", to: "https://my.blytzpay.com/#/merchant/7acdb786-1f15-4eca-aff5-f4b1fea02516/login", testid: "care-link-make-payment" },
      { label: "Call Your Account Manager", to: "tel:+12142714800", testid: "care-link-call-manager" },
    ],
  },
  {
    icon: Wrench,
    title: "Servicing Your Vehicle",
    testid: "care-category-vehicle",
    links: [
      {
        label: "Contact Our Service Centers",
        to: "#vehicle-service",
        testid: "care-link-service-centers",
      },
      {
        label: "Warranty Information",
        to: "#warranty",
        testid: "care-link-warranty",
      },
      { label: "Afterhours Towing", to: "tel:+12143280300", testid: "care-link-afterhours-towing" },
    ],
  },
  {
    icon: LifeBuoy,
    title: "Customer Support",
    testid: "care-category-support",
    links: [
      { label: "Open a Support Ticket", to: "#customer-support", testid: "care-link-support-ticket" },
      {
        label: "Customer Handbook",
        to: "#handbook",
        testid: "care-link-handbook",
      },
    ],
  },
];

const CareLink = ({ link }) => {
  const cls =
    "group/link flex items-center justify-between gap-4 py-3.5 border-b border-slate-900/10 text-slate-700 hover:text-slate-900 transition-colors duration-300";
  const inner = (
    <>
      <span className="text-sm font-medium">{link.label}</span>
      <ArrowUpRight
        size={15}
        className="text-ember shrink-0 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
      />
    </>
  );
  return link.internal ? (
    <Link to={link.to} data-testid={link.testid} className={cls}>
      {inner}
    </Link>
  ) : (
    <a href={link.to} data-testid={link.testid} className={cls}>
      {inner}
    </a>
  );
};

const CustomerCare = () => (
  <>
    <PageHeader
      testid="customer-care-header"
      overline="Customer Care Portal"
      title="Welcome to"
      accent="Customer Care."
      description="Concorde Auto Acceptance has endeavored to make great customer service the cornerstone of our business. We take the time to know our customers, discuss their needs and do everything we can to help."
    />

    <section className="pb-24 lg:pb-32" data-testid="customer-care-categories">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div
              data-testid={c.testid}
              className="h-full rounded-2xl border border-slate-900/10 bg-white/75 backdrop-blur-xl p-8 transition-colors duration-300 hover:border-ember/40"
            >
              <c.icon size={26} className="text-ember" />
              <h2 className="mt-6 font-display font-bold text-xl text-slate-900 tracking-tight">
                {c.title}
              </h2>
              <div className="mt-4">
                {c.links.map((l) => (
                  <CareLink key={l.label} link={l} />
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section id="vehicle-service" className="py-24 lg:py-32 bg-[#EDF2FA] border-y border-slate-900/10" data-testid="customer-care-vehicle-service">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Overline>Vehicle Service</Overline>
          <h2 className="mt-6 font-display font-bold tracking-tight text-4xl sm:text-5xl text-slate-900 max-w-2xl">
            Contact our service centers.
          </h2>
          <div className="mt-8 max-w-3xl text-slate-600 leading-relaxed" data-testid="vehicle-service-verbiage">
            <p>Use this form to:</p>
            <ul className="mt-4 space-y-2 list-disc list-inside">
              <li>Request a service appointment</li>
              <li>Check the status of a repair</li>
              <li>Schedule routine maintenance</li>
              <li>Request a tow (non-emergency)</li>
              <li>Contact a service representative</li>
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            className="mt-12 rounded-2xl border border-slate-900/10 bg-white overflow-hidden"
            data-testid="vehicle-service-iframe-wrapper"
          >
            <iframe
              src="https://form.jotform.com/210335849884163"
              title="Auto City Service Contact Form"
              data-testid="vehicle-service-iframe"
              className="w-full h-[1400px] border-0"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section id="warranty" className="py-24 lg:py-32" data-testid="customer-care-warranty">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Overline>Warranty Information</Overline>
          <h2 className="mt-6 font-display font-bold tracking-tight text-4xl sm:text-5xl text-slate-900 max-w-2xl">
            Your warranty, explained.
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed max-w-3xl">
            Browse warranty coverage details and answers to common warranty
            questions below — without leaving our site.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            className="mt-12 rounded-2xl border border-slate-900/10 bg-white overflow-hidden"
            data-testid="warranty-iframe-wrapper"
          >
            <iframe
              src="https://autocity.freshdesk.com/support/solutions/folders/9000184128"
              title="Warranty Information"
              data-testid="warranty-iframe"
              className="w-full h-[900px] border-0"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section id="handbook" className="py-24 lg:py-32 bg-[#EDF2FA] border-y border-slate-900/10" data-testid="customer-care-handbook">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Overline>Customer Handbook</Overline>
          <h2 className="mt-6 font-display font-bold tracking-tight text-4xl sm:text-5xl text-slate-900 max-w-2xl">
            Everything about your account, in one handbook.
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed max-w-3xl">
            The customer handbook covers your contract, payments, insurance,
            and vehicle care — read it right here.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            className="mt-12 rounded-2xl border border-slate-900/10 bg-white overflow-hidden"
            data-testid="handbook-iframe-wrapper"
          >
            <iframe
              src="https://autocity.freshdesk.com/support/solutions"
              title="Customer Handbook"
              data-testid="handbook-iframe"
              className="w-full h-[900px] border-0"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section id="customer-support" className="py-24 lg:py-32" data-testid="customer-care-ticket">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Overline>Customer Support Ticket</Overline>
          <h2 className="mt-6 font-display font-bold tracking-tight text-4xl sm:text-5xl text-slate-900 max-w-2xl">
            Open a support ticket.
          </h2>
          <div className="mt-8 max-w-3xl space-y-4 text-slate-600 leading-relaxed" data-testid="ticket-verbiage">
            <p>
              Thank you for taking the time to submit a support ticket. We value
              you as a customer and look forward to working with you to help
              resolve your concerns.
            </p>
            <p>
              Please allow us at least 24 business hours to review your ticket
              and respond with any options we can offer.
            </p>
            <p className="font-bold text-slate-900">
              IMPORTANT: All communication with the support department is done
              in writing via email so that other individuals and departments can
              be copied, so please check this email frequently.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            className="mt-12 rounded-2xl border border-slate-900/10 bg-white overflow-hidden"
            data-testid="support-ticket-iframe-wrapper"
          >
            <iframe
              src="https://autocity.freshdesk.com/support/tickets/new"
              title="Customer Support Desk — Submit a ticket"
              data-testid="support-ticket-iframe"
              className="w-full h-[900px] border-0"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default CustomerCare;
